<template>
  <section class="main">
    <loader v-if="!configLoaded" />
    <h1 v-if="configLoaded" class="intro">
      <span class="intro__inner">{{ $t("PRO_STATUS_FOR_DEVICE") }}</span>
      <template
        v-if="
          config.is_apple_silicon === 'yes' &&
          parseFloat(config.ios_version) < 11.3
        "
      >
        <span class="intro__status g-status g-status_errors">
          {{ $t("NOT_APPLICABLE") }}
        </span>
      </template>
      <template v-else>
        <span
          class="intro__status g-status"
          :class="{
            'g-status_success': config.is_pro === 'yes',
            'g-status_error': config.is_pro_failure_reason === 'revoked',
          }"
        >
          {{ $t(status) }}
        </span>
      </template>
    </h1>
    <div v-if="configLoaded" class="row">
      <div class="col col_1">
        <template
          v-if="
            config.model.match(/mac/i) && parseFloat(config.ios_version) < 11.3
          "
        >
          <html-with-links
            class="error error_mb g-status g-status_error"
            tag="p"
            :html="
              $t('UPGRADE_MACOS_TO_USE', {
                rickpactorLink: '/app/standalone/1900000033',
              })
            "
          ></html-with-links>

          <div class="provided provided_alone">
            <a
              class="provided__link"
              href="https://aysa-support.freshdesk.com/"
              target="_blank"
            >
              <img class="provided__icon" src="@/assets/icon-support.svg" />
              {{ $t("GET_SUPPORT") }}
            </a>
            <span class="provided__support">
              {{ $t("SUPPORT_BY_3RD_PARTY") }}
            </span>
          </div>
        </template>
        <template
          v-else-if="
            config.model.match(/mac/i) && config.is_apple_silicon !== 'yes'
          "
        >
          <div class="content">
            <html-with-links
              tag="p"
              class="text"
              :html="
                $t('INTEL_MACS_DO_NOT_SUPPORT_PRO', {
                  rickpactorLink: '/app/standalone/1900000033',
                })
              "
            ></html-with-links>
          </div>
        </template>
        <template v-else>
          <template
            v-if="config.is_pro === 'yes' || config.is_pro_failure_reason"
          >
            <div
              v-if="config.is_pro_failure_reason === 'disabled'"
              class="error-wrapper"
            >
              <p class="error g-status g-status_error">
                {{ $t("PRO_IS_DISABLED_BY_YOU") }}
              </p>
              <btn
                @click.native="enablePro"
                :loading="loading"
                type="primary"
                >{{ $t("ENABLE") }}</btn
              >
            </div>

            <div
              v-else-if="config.is_pro_failure_reason === 'pending'"
              class="error-wrapper"
            >
              <p class="error g-status g-status_error">
                {{ $t("PRO_IS_PENDING") }}
              </p>
            </div>

            <div
              v-else-if="config.is_pro_failure_reason === 'pending_apple'"
              class="error-wrapper"
            >
              <template v-if="config.is_pro_failure_reason_data.ineligible_till">
              <p class="error g-status g-status_error">
                {{ $t("PRO_IS_PENDING_APPLE_INELIGIBLE",config.is_pro_failure_reason_data) }}
              </p>
              </template>
              <template v-else>
                <p class="error g-status g-status_error">
                  {{ $t("PRO_IS_PENDING_APPLE_WAIT",config.is_pro_failure_reason_data) }}
                </p>
              </template>
            </div>

            <div
              v-else-if="config.is_pro_failure_reason === 'expired'"
              class="error-wrapper"
            >
              <html-with-links
                tag="p"
                class="error g-status g-status_error"
                :html="
                  $t('PRO_EXPIRED_YOU_CAN_GET_IT', {
                    date: new Date(config.pro_till).toLocaleString(),
                    proLink: '/my/buy',
                  })
                "
              ></html-with-links>
            </div>

            <div v-else-if="config.is_pro_failure_reason === 'revoked'">
              <div class="error-wrapper">
                <p class="error g-status g-status_error">
                  {{ $t("PRO_REVOKED") }}
                </p>
                <btn
                  @click.native="reactivatePro"
                  :loading="loading"
                  type="primary"
                >
                  {{ $t("ACTIVATE") }}
                </btn>
              </div>

              <ul class="list">
                <li class="list__item">
                  {{ $t("PRO_REVOKED_DESC_1") }}
                </li>
                <li class="list__item">
                  {{ $t("PRO_REVOKED_DESC_2") }}
                </li>
              </ul>
            </div>

            <table
              class="table"
              v-if="config.pro_till || config.pro_voucher_activated_at"
            >
              <tr v-if="config.pro_till">
                <td class="cell">{{ $t("PRO_EXPIRES_ON") }}</td>
                <td class="td">
                  {{ new Date(config.pro_till).toLocaleString() }}
                </td>
              </tr>
              <tr v-if="config.pro_voucher_activated_at">
                <td class="cell">{{ $t("VOUCHER_ACTIVATED_ON") }}</td>
                <td class="td">
                  {{
                    new Date(config.pro_voucher_activated_at).toLocaleString()
                  }}
                  ({{ $t("DAYS_AGO", { count: voucherActivatedAgo }) }})
                </td>
              </tr>
            </table>

            <div class="provided">
              <span class="cell provided__desc">
                {{ $t("PRO_PROVIDED_BY") }}
              </span>
              <div class="provided__val">
                {{ config.pro_provider }}
                <a
                  class="provided__link"
                  :href="config.pro_support_uri"
                  target="_blank"
                >
                  <img class="provided__icon" src="@/assets/icon-support.svg" />
                  {{ $t("GET_SUPPORT") }}
                </a>
                <span class="provided__support">
                  {{ $t("SUPPORT_BY_3RD_PARTY") }}
                </span>
              </div>
            </div>
          </template>
          <template v-else>
            <html-with-links
              tag="p"
              class="error error_mb g-status g-status_error"
              :html="
                $t('PRO_NOT_ACTIVATED_YOU_CAN_GET_IT', { buyLink: '/my/buy' })
              "
            ></html-with-links>

            <div class="provided provided_alone">
              <a
                class="provided__link"
                href="https://aysa-support.freshdesk.com/"
                target="_blank"
              >
                <img class="provided__icon" src="@/assets/icon-support.svg" />
                {{ $t("GET_SUPPORT") }}
              </a>
              <span class="provided__support">
                {{ $t("SUPPORT_BY_3RD_PARTY") }}
              </span>
            </div>
          </template>
        </template>
      </div>
      <aside class="sidebar col col_2">
        <h2 class="sidebar__heading">{{ $t("WHAT_IS_APPDB_PRO") }}</h2>
        <p class="sidebar__text g-text" v-html="$t('PRO_FEATURES_DESC')"></p>
      </aside>
    </div>
  </section>
</template>

<script>
import Btn from '@/components/Button';
import Loader from '@/components/Loader';
import HtmlWithLinks from '@/components/HtmlWithLinks.vue';
import moment from 'moment';

export default {
  components: { Btn, Loader, HtmlWithLinks },
  data() {
    return {
      loading: false,
    };
  },
  computed: {
    title() {
      return this.$t('VIEW_PRO_STATUS');
    },
    configLoaded() {
      return !!this.config.model;
    },
    config() {
      return this.$store.getters.config;
    },
    status() {
      if (this.config.is_pro === 'yes') {
        return 'ACTIVATED';
      }

      if (this.config.is_pro_failure_reason === 'revoked') {
        return 'REVOKED';
      }

      if (this.config.is_pro_failure_reason === 'pending') {
        return 'PENDING';
      }

      if (this.config.is_pro_failure_reason === 'pending_apple') {
        return 'PENDING_APPLE';
      }

      return 'NOT_ACTIVATED';
    },
    voucherActivatedAgo() {
      const date = this.config.pro_voucher_activated_at;
      if (!date) {
        return null;
      }
      const days = moment().diff(date, 'days');
      return days;
    },
  },
  methods: {
    enablePro() {
      this.loading = true;
      this.$api.saveConfiguration({ is_pro_disabled: 'no' })
        .then(() => this.$store.dispatch('getConfig'))
        .finally(() => {
          this.loading = false;
        });
    },
    reactivatePro() {
      this.loading = true;
      this.$api.reactivatePro()
        .then(() => Promise.all(this.$store.dispatch('getConfig'),
          this.$store.dispatch('getDevices')))
        .then(() => {
          this.$notify({
            type: 'success',
            text: this.$t('PRO_REACTIVATION_SUCCESS', { ipaCacheLink: '/my/ipa-cache' }),
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/variables";

.error-wrapper {
  display: flex;
  margin-bottom: 15px;
}

.error {
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  margin-right: 5px;
  flex-grow: 1;
  font-size: 14px;

  &_mb {
    margin-bottom: 20px;

    @include tablet {
      margin-bottom: 15px;
    }
  }
}

.main {
  display: block;
  flex-grow: 1;
}

.intro {
  &__inner {
    margin-right: 5px;
  }

  &__status {
    font-size: inherit;
    font-weight: 400;
  }
}

.row {
  display: flex;
  align-items: flex-start;

  @include notebook {
    flex-wrap: wrap;
  }
}

.col {
  &_1 {
    flex-grow: 1;
  }

  &_2 {
    flex: 0 0 192px;
    margin-left: 15px;

    @include notebook {
      margin-left: 0;
      margin-top: 15px;
    }
  }

  @include notebook {
    flex: 0 0 100%;
  }
}

.table {
  line-height: 18px;
  margin-bottom: 15px;
}

.td {
  padding-left: 40px;
}

.cell {
  width: 140px;
  flex: 0 0 140px;
}

.provided {
  padding-top: 15px;
  border-top: 1px solid #e5e5e5;
  display: flex;

  &_alone {
    flex-direction: column;

    .provided__support {
      max-width: 100%;
    }

    .provided__link {
      margin-top: 0;
    }
  }

  &__val {
    padding-left: 40px;
    font-weight: 500;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }

  &__link {
    color: $text-primary;
    display: flex;
    align-items: center;
    margin: 7px 0;
  }

  &__icon {
    margin-right: 5px;
  }

  &__support {
    font-size: 12px;
    font-weight: 400;
    max-width: 300px;
  }
}

.button {
  margin-left: 5px;
}

.sidebar {
  background: #fbfbfb;
  border-radius: 8px;
  padding: 10px;

  &__heading {
    font-weight: 500;
    margin-bottom: 10px;
  }

  &__text {
    &::v-deep {
      p:not(:last-child) {
        margin-bottom: 15px;
      }
    }
  }
}

.list {
  list-style: disc inside;
  margin-bottom: 15px;

  &__item {
    &:not(:last-child) {
      margin-bottom: 5px;
    }
  }
}
</style>
